import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useDialogKeys } from "../despacho/Cuenta";

/**
 * Diálogo modal del sistema con su velo (`sk-scrim` + `sk-modal-layer` +
 * `sk-modal`), como «Elegir áreas» de biblioteca.html: nombrado por su
 * título y descrito por su subtítulo, el foco entra (al selector `initial`
 * o al título), Tab no se escapa, Escape y el velo cierran, y al cerrar el
 * foco vuelve al mando que lo abrió (o al de `returnFocus`).
 * Frente a `Modal` (la caja sola, que coloca la app con `pos-dialog`): para
 * un diálogo nuevo con velo, este; `Modal` queda para Preferencias.
 * Se pinta en un hueco propio al final de `<body>` y, mientras está
 * abierto, TODO lo demás del documento va `inert`: ni el ratón, ni el
 * teclado, ni el lector llegan al fondo.
 */
export function Dialog({ open, title, subtitle, onClose, footer, initial, returnFocus, children }: {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode | undefined;
  onClose: () => void;
  footer?: ReactNode | undefined;
  /** Selector del mando que recibe el foco al abrir; por defecto, el título. */
  initial?: string | undefined;
  returnFocus?: string | undefined;
  children?: ReactNode;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const id = useId();
  const [host] = useState<HTMLDivElement | null>(() => (typeof document === "undefined" ? null : document.createElement("div")));
  // ANTES que las teclas del diálogo: el hueco ha de estar en el documento cuando el foco entre
  useEffect(() => {
    if (!open || host === null) return;
    // el mando que lo abrió, ANTES de volver inerte el fondo (inerte, pierde el foco)
    const antes = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    document.body.appendChild(host);
    const fondo = [...document.body.children].filter((e) => e !== host && !e.hasAttribute("inert"));
    for (const e of fondo) e.setAttribute("inert", "");
    return () => {
      for (const e of fondo) e.removeAttribute("inert");
      host.remove();
      const volver = () => { const el = returnFocus ? document.querySelector<HTMLElement>(returnFocus) ?? antes : antes; if (el?.isConnected) el.focus(); };
      volver();
      requestAnimationFrame(() => { if (document.activeElement === document.body || document.activeElement === null) volver(); });
    };
  }, [open, host, returnFocus]);
  useDialogKeys(caja, open, onClose, true, initial ?? `[id="${id}-t"]`, returnFocus);
  if (!open || host === null) return null;
  return createPortal(
    <>
      <div className="sk-scrim sk-open" onClick={onClose} />
      <div className="sk-modal-layer sk-open">
        <div ref={caja} className="sk-modal" role="dialog" aria-modal="true" aria-labelledby={`${id}-t`} aria-describedby={subtitle !== undefined ? `${id}-s` : undefined}>
          <h2 className="sk-h2" id={`${id}-t`} tabIndex={-1}>{title}</h2>
          {subtitle !== undefined && <p className="sk-modal-sub" id={`${id}-s`}>{subtitle}</p>}
          {children}
          {footer !== undefined && <div className="sk-modal-foot">{footer}</div>}
        </div>
      </div>
    </>,
    host,
  );
}
